import { useState } from 'react';
import type { CSSProperties } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { PRICING } from '@vault/shared';
import { Badge } from '@vault/ui';
import { api, ApiError } from '../api/client.js';
import { errMessage, useToasts } from './_vault/shared.js';
import {
  ActiveSection,
  BillingError,
  BillingSkeleton,
  CancelModal,
  CancelledSection,
  DunningBanner,
  FreeSection,
  PastDueSection,
} from './billing/parts.js';
import type { Interval } from './billing/parts.js';

/**
 * Billing hub — sibling of Settings. Renders exactly one state section from
 * the subscription status:
 *   active     → plan card + payment-method management (billing portal)
 *   past_due   → dunning banner + "update payment method" card (portal)
 *   cancelled  → win-back card, reactivation goes through checkout
 *   none       → Free-vs-Pro mini table + upgrade via checkout
 * Portal and checkout both hand off in the SAME tab (window.location.href).
 * When billing isn't wired yet the API answers 501 — we say so plainly.
 */
export function BillingPage() {
  const toasts = useToasts();
  const [interval, chooseInterval] = useState<Interval>('yearly');
  const [cancelOpen, setCancelOpen] = useState(false);

  const sub = useQuery({ queryKey: ['billing', 'subscription'], queryFn: () => api.billing.subscription() });
  const usage = useQuery({ queryKey: ['usage'], queryFn: () => api.account.usage() });
  const plans = useQuery({ queryKey: ['billing', 'plans'], queryFn: () => api.billing.plans() });
  const pricing = plans.data?.pricing.pro ?? PRICING.pro;

  const portal = useMutation({
    mutationFn: () => api.billing.portal(),
    onSuccess: (res) => {
      window.location.href = res.url;
    },
    onError: (err) => {
      // The subscription payload may already carry a portal link — use it
      // before giving up so "update card" never dead-ends during dunning.
      const fallback = sub.data?.portalUrl;
      if (fallback) {
        window.location.href = fallback;
        return;
      }
      if (err instanceof ApiError && (err.status === 501 || err.code === 'billing_not_configured')) {
        toasts.show({
          tone: 'warn',
          title: 'Billing portal is opening soon',
          description: 'Hang tight — we’ll email you the moment it’s live.',
        });
        return;
      }
      toasts.show({ tone: 'danger', title: 'Couldn’t open the billing portal', description: errMessage(err) });
    },
  });

  const checkout = useMutation({
    mutationFn: (iv: Interval) => api.billing.checkout(iv),
    onSuccess: (res) => {
      window.location.href = res.url;
    },
  });
  const checkoutSoon =
    checkout.isError &&
    checkout.error instanceof ApiError &&
    (checkout.error.status === 501 || checkout.error.code === 'billing_not_configured');
  const checkoutFailed = checkout.isError && !checkoutSoon;

  const s = sub.data;
  const plan = usage.data?.plan ?? (s?.status === 'active' || s?.status === 'past_due' ? 'pro' : 'free');

  return (
    <div style={page}>
      <style>{BILLING_CSS}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <h1 style={heading}>Billing</h1>
        <Badge tone={plan === 'pro' ? 'pro' : 'neutral'}>{plan === 'pro' ? 'PRO' : 'FREE'}</Badge>
      </div>

      {sub.isPending ? (
        <BillingSkeleton />
      ) : sub.isError ? (
        <BillingError message={errMessage(sub.error)} retrying={sub.isFetching} onRetry={() => void sub.refetch()} />
      ) : s ? (
        <>
          {s.status === 'past_due' && (
            <>
              <DunningBanner graceEndsAt={s.renewsAt} busy={portal.isPending} onUpdateCard={() => portal.mutate()} />
              <PastDueSection sub={s} busy={portal.isPending} onPortal={() => portal.mutate()} />
            </>
          )}

          {s.status === 'active' && (
            <>
              <ActiveSection
                sub={s}
                pricing={pricing}
                portalBusy={portal.isPending}
                onPortal={() => portal.mutate()}
                onCancel={() => setCancelOpen(true)}
              />
              <CancelModal
                open={cancelOpen}
                onClose={() => setCancelOpen(false)}
                keepsUntil={s.renewsAt}
                busy={portal.isPending}
                onPortal={() => portal.mutate()}
              />
            </>
          )}

          {s.status === 'cancelled' && (
            <CancelledSection
              sub={s}
              pricing={pricing}
              interval={interval}
              onInterval={chooseInterval}
              busy={checkout.isPending}
              onReactivate={() => checkout.mutate(interval)}
              checkoutSoon={checkoutSoon}
              checkoutFailed={checkoutFailed}
            />
          )}

          {s.status === 'none' && (
            <FreeSection
              pricing={pricing}
              interval={interval}
              onInterval={chooseInterval}
              busy={checkout.isPending}
              onUpgrade={() => checkout.mutate(interval)}
              checkoutSoon={checkoutSoon}
              checkoutFailed={checkoutFailed}
            />
          )}
        </>
      ) : null}

      {toasts.node}
    </div>
  );
}

const page: CSSProperties = {
  maxWidth: 760,
  margin: '0 auto',
  padding: '24px 28px',
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
};
const heading: CSSProperties = {
  margin: 0,
  fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 600, letterSpacing: '-0.025em',
};

const BILLING_CSS = `
@media (max-width:640px){.tvbl-plans{flex-direction:column}.tvbl-cta{justify-content:center}}
`;
